/**
 * Componente de sección de Histórico
 * History section component
 */

import { faChartLine, faCheckCircle, faClock, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { GetRowIdParams } from 'ag-grid-community';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { HistoryFailedRecord, HistorySummaryResponse } from '../../models/TrackingApiResponse';

interface HistorySectionProps {
  summary?: HistorySummaryResponse;
  failed?: HistoryFailedRecord[];
  total?: number;
  isLoading?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  onRetry: (orderIds: number[]) => Promise<void>;
  isRetrying?: boolean;
}

export const HistorySection = ({
  summary,
  failed = [],
  total = 0,
  isLoading = false,
  isFetching = false,
  isFetchingNextPage = false,
  fetchNextPage,
  hasNextPage = false,
  onRetry,
  isRetrying = false,
}: HistorySectionProps) => {
  const { t } = useTranslation('trackingModule');
  const { filteredColumnDefs } = useAgGridColumns({
    tableType: 'historyFailed',
  });
  const gridRef = useRef<AgGridTableRef>(null);
  const [selectedRows, setSelectedRows] = useState<HistoryFailedRecord[]>([]);

  const handleSelectionChanged = (rows: HistoryFailedRecord[]) => {
    setSelectedRows(rows);
  };

  const handleRetry = async () => {
    if (selectedRows.length === 0) {
      return;
    }
    const orderIds = selectedRows.map((row) => row.order_id);
    await onRetry(orderIds);
    // Limpiar selección después del reintento
    setSelectedRows([]);
    if (gridRef.current) {
      gridRef.current.deselectAll();
    }
  };

  return (
    <div className="space-y-6">
      {/* Descripción general */}
      <SectionBox data-testid="history-description-section">
        <h2 className="text-lg font-semibold text-title mb-3">{t('history.title')}</h2>
        <p className="text-sm text-complementary-text leading-relaxed">{t('history.description')}</p>
      </SectionBox>

      {/* Resumen */}
      <SectionBox data-testid="history-summary-section">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-semibold text-title">{t('history.summary.title')}</h2>
          <InfoTooltip
            text={t('history.summary.description')}
            dataTestId="history-summary-info"
            iconClassName="w-4 h-4"
          />
        </div>

        {isLoading && !summary ? (
          <div className="text-center py-8 text-subtext">{t('loading.message')}</div>
        ) : !summary ? (
          <EmptyView
            type="emptyState"
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={t('empty.no_data.title')}
            description={t('empty.no_data.description')}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SectionBox data-testid="metric-completed">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={faCheckCircle} className="w-3.5 h-3.5 text-success" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-complementary-text">
                      {t('history.summary.total_completed')}
                    </h3>
                    <InfoTooltip
                      text={t('history.summary.total_completed_tooltip')}
                      dataTestId="completed-tooltip"
                      iconClassName="w-3 h-3"
                    />
                  </div>
                  <p className="text-base font-bold text-title">{summary.total_completed}</p>
                </div>
              </div>
            </SectionBox>
            <SectionBox data-testid="metric-pending">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={faClock} className="w-3.5 h-3.5 text-warning" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-complementary-text">
                      {t('history.summary.total_pending')}
                    </h3>
                    <InfoTooltip
                      text={t('history.summary.total_pending_tooltip')}
                      dataTestId="pending-tooltip"
                      iconClassName="w-3 h-3"
                    />
                  </div>
                  <p className="text-base font-bold text-title">{summary.total_pending}</p>
                </div>
              </div>
            </SectionBox>
            <SectionBox data-testid="metric-failed">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="w-3.5 h-3.5 text-error" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-complementary-text">{t('history.summary.total_failed')}</h3>
                    <InfoTooltip
                      text={t('history.summary.total_failed_tooltip')}
                      dataTestId="failed-tooltip"
                      iconClassName="w-3 h-3"
                    />
                  </div>
                  <p className="text-base font-bold text-title">{summary.total_failed}</p>
                </div>
              </div>
            </SectionBox>
            <SectionBox data-testid="metric-success-rate">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-card-bg flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={faChartLine} className="w-3.5 h-3.5 text-info" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-complementary-text">{t('history.summary.success_rate')}</h3>
                    <InfoTooltip
                      text={t('history.summary.success_rate_tooltip')}
                      dataTestId="success-rate-tooltip"
                      iconClassName="w-3 h-3"
                    />
                  </div>
                  <p className="text-base font-bold text-title">{summary.success_rate.toFixed(1)}%</p>
                </div>
              </div>
            </SectionBox>
          </div>
        )}
      </SectionBox>

      {/* Registros Fallidos */}
      <SectionBox data-testid="history-failed-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-title">{t('history.failed.title')}</h2>
          {selectedRows.length > 0 && (
            <Button onClick={handleRetry} disabled={isRetrying} variant="primary">
              {isRetrying ? t('loading.message') : `${t('history.failed.retry_selected')} (${selectedRows.length})`}
            </Button>
          )}
        </div>
        <p className="text-sm text-subtext mb-4">{t('history.failed.description')}</p>

        {isLoading && failed.length === 0 ? (
          <div className="text-center py-8 text-subtext">{t('loading.message')}</div>
        ) : total === 0 ? (
          <EmptyView
            type="emptyState"
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={t('empty.no_data.title')}
            description={t('empty.no_data.description')}
          />
        ) : (
          <div
            className={`transition-opacity duration-300 ${
              isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
            }`}
          >
            <AgGridTable
              ref={gridRef}
              data={failed}
              columnDefs={filteredColumnDefs}
              height="calc(100vh - 350px)"
              enableSelection={true}
              onSelectionChanged={handleSelectionChanged}
              rowHeight={60}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              gridOptions={{
                getRowId: (params: GetRowIdParams) => `history-failed-${params.data.id}`,
              }}
            />
          </div>
        )}
      </SectionBox>
    </div>
  );
};
